/**
 * BalanceDetail Component
 *
 * Displays the full token portfolio for a selected wallet:
 *   1. created-tokens        → token metadata (symbol/name/decimals)
 *   2. wallet-balances       → held balances per tokenId (balance > 0 = Owned)
 *   3. escrow balance        → USDb escrow-locked amount (splits the Balance Breakdown)
 *   4. exchange rates        → USD value per non-USD token
 *
 * All LP API calls are proxied through Rails admin page_action endpoints.
 * No LP API credentials or secrets are ever sent to the browser.
 *
 * Owned = tokens actually held (any balance); Available = the rest of the
 * created tokens. USD value is computed client-side (stablecoins 1:1, others
 * via the fiat rate).
 *
 * @component
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { API_ENDPOINTS } from "./constants";
import {
  commonStyles,
  colors,
  gradients,
  buttonStyles,
  statsCardStyles,
  paginationStyles,
  loadingStyles,
} from "../AgenticPaymentTransactionHistory/styles";
import { getRequest } from "../../utils/httpRequest";

// ============================================================================
// Helper: Format raw balance to human readable
// ============================================================================

const formatBalance = (rawBalance) => {
  if (!rawBalance || rawBalance === "0") return "0.00";
  const value = parseFloat(rawBalance);
  if (isNaN(value)) return "0.00";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });
};

// Convert token balance to a number
const getHumanBalance = (rawBalance) => {
  if (!rawBalance || rawBalance === "0") return 0;
  return parseFloat(rawBalance) || 0;
};

// ============================================================================
// Helper: Token symbol → fiat code mapping
// ============================================================================

const getFiatCode = (symbol) => {
  if (!symbol) return null;
  // USDC and USD and USDb are always 1:1
  if (symbol === "USDC" || symbol === "USD" || symbol === "USDb") return null;
  // Tokens ending in 'b' map to fiat: EURb → EUR, GBPb → GBP
  if (symbol.endsWith("b")) return symbol.slice(0, -1).toUpperCase();
  return symbol.toUpperCase();
};

const formatUsd = (value) => {
  if (value == null || isNaN(value)) return "$0.00";
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// ============================================================================
// Exchange Rate Cache (60s TTL)
// ============================================================================

const rateCache = { data: {}, timestamp: null, ttl: 60000 };

// ============================================================================
// Sub-components
// ============================================================================

const TabButton = ({ active, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: "10px 24px",
      background: active ? colors.primary : colors.white,
      color: active ? colors.white : colors.gray700,
      border: `1px solid ${active ? colors.primary : colors.border}`,
      borderBottom: active ? "none" : `1px solid ${colors.border}`,
      borderRadius: "6px 6px 0 0",
      fontSize: "14px",
      fontWeight: active ? 700 : 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: active ? "-1px" : "0",
      position: "relative",
      zIndex: active ? 1 : 0,
    }}
  >
    {label}
    {count !== undefined && (
      <span
        style={{
          background: active ? "rgba(255,255,255,0.2)" : colors.gray300,
          color: active ? colors.white : colors.gray600,
          padding: "2px 8px",
          borderRadius: "10px",
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {count}
      </span>
    )}
  </button>
);

// Escrow summary card, shown when USDb escrow data is available
// Portfolio balance breakdown: total of ALL owned assets, split into available
// vs escrow-locked. Escrow only exists for USDb, so when a wallet has no locked
// escrow, available == the full portfolio total.
const EscrowSummaryCard = ({ totalBalanceUSD, escrowData }) => {
  const total = totalBalanceUSD || 0;
  const escrow = escrowData ? parseFloat(escrowData.escrow) || 0 : 0;
  const available = Math.max(0, total - escrow);
  const hasEscrow = escrow > 0;

  if (total <= 0 && !escrowData) return null;

  const cardStyle = {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "20px",
  };

  const titleStyle = {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontWeight: 600,
    color: colors.gray800,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const statRowStyle = {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  };

  const statItemStyle = (bgColor) => ({
    flex: 1,
    minWidth: "140px",
    padding: "14px 18px",
    borderRadius: "6px",
    background: bgColor,
  });

  const statLabelStyle = {
    display: "block",
    fontSize: "11px",
    textTransform: "uppercase",
    fontWeight: 600,
    letterSpacing: "0.5px",
    marginBottom: "4px",
  };

  const statValueStyle = {
    display: "block",
    fontSize: "22px",
    fontWeight: 700,
  };

  return (
    <div style={cardStyle}>
      <h3 style={titleStyle}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={colors.primary}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M12 10v4" />
          <path d="M10 12h4" />
        </svg>
        Balance Breakdown
      </h3>
      <div style={statRowStyle}>
        <div style={statItemStyle("#f0f4ff")}>
          <span style={{ ...statLabelStyle, color: colors.primary }}>
            Total Balance
          </span>
          <span style={{ ...statValueStyle, color: colors.primary }}>
            ${total.toFixed(2)}
          </span>
        </div>
        <div style={statItemStyle("#f0fdf4")}>
          <span style={{ ...statLabelStyle, color: "#16a34a" }}>Available</span>
          <span style={{ ...statValueStyle, color: "#16a34a" }}>
            ${available.toFixed(2)}
          </span>
        </div>
        {hasEscrow && (
          <div style={statItemStyle("#fff7ed")}>
            <span style={{ ...statLabelStyle, color: "#ea580c" }}>
              In Escrow
            </span>
            <span style={{ ...statValueStyle, color: "#ea580c" }}>
              ${escrow.toFixed(2)}
            </span>
            <span
              style={{
                fontSize: "11px",
                color: colors.gray500,
                marginTop: "4px",
                display: "block",
              }}
            >
              Locked in active offers or pending transactions
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const TokenTable = ({
  tokens,
  loading,
  emptyMessage,
  escrowData,
  exchangeRates,
  totalBalanceUSD,
  ratesLoading,
}) => {
  if (tokens.length === 0 && !loading) {
    return (
      <div className="blank_slate">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  const showUsdColumns = tokens.some((t) => t.isOwned);

  return (
    <div style={{ ...commonStyles.panel, marginBottom: 0 }}>
      <div className="index_table_container">
        <table className="index_table">
          <thead>
            <tr>
              <th className="col">Token Symbol</th>
              <th className="col">Token Name</th>
              <th className="col">Token ID</th>
              <th className="col">Balance</th>
              {showUsdColumns && (
                <>
                  <th className="col">Rate</th>
                  <th className="col">USD Value</th>
                  <th className="col">Portfolio %</th>
                </>
              )}
              <th className="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((token, idx) => {
              // Escrow info for USDb
              const isUSDb = token.symbol === "USDb" && escrowData;
              const escrowAmount = isUSDb
                ? parseFloat(escrowData.escrow) || 0
                : 0;
              const availableAmount = isUSDb
                ? parseFloat(escrowData.available) || 0
                : 0;
              const hasEscrow = escrowAmount > 0;

              // USD calculations
              const humanBal = getHumanBalance(token.rawBalance);
              const fiatCode = getFiatCode(token.symbol);
              const rate = fiatCode
                ? exchangeRates[token.symbol] || null
                : null;
              const isStable = !fiatCode; // USDC, USD, USDb
              const usdValue = isStable
                ? humanBal
                : rate
                ? humanBal / rate
                : null;
              const pctOfPortfolio =
                totalBalanceUSD > 0 && usdValue != null
                  ? (usdValue / totalBalanceUSD) * 100
                  : null;

              return (
                <tr
                  key={token.tokenId}
                  className={idx % 2 === 0 ? "even" : "odd"}
                >
                  <td className="col">
                    <strong>{token.symbol || "-"}</strong>
                  </td>
                  <td className="col">{token.name || "-"}</td>
                  <td className="col">
                    <code>{token.tokenId}</code>
                  </td>
                  <td className="col">
                    {token.loadingBalance ? (
                      <span
                        style={{
                          color: colors.gray500,
                          fontSize: "13px",
                          fontStyle: "italic",
                        }}
                      >
                        Loading...
                      </span>
                    ) : token.error ? (
                      <span
                        style={{
                          color: colors.danger,
                          fontSize: "13px",
                        }}
                        title={token.error}
                      >
                        Failed
                      </span>
                    ) : isUSDb ? (
                      <div>
                        <strong>{formatBalance(token.rawBalance)}</strong>
                        {hasEscrow && (
                          <div
                            style={{
                              fontSize: "11px",
                              marginTop: "4px",
                              lineHeight: "1.5",
                            }}
                          >
                            <span style={{ color: "#16a34a" }}>
                              Available: ${availableAmount.toFixed(2)}
                            </span>
                            <br />
                            <span style={{ color: "#ea580c" }}>
                              Escrow: ${escrowAmount.toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <strong>{formatBalance(token.rawBalance)}</strong>
                    )}
                  </td>
                  {showUsdColumns && (
                    <>
                      <td className="col">
                        {isStable ? (
                          <span style={{ color: colors.gray500 }}>1.0000</span>
                        ) : rate ? (
                          <span>{rate.toFixed(4)}</span>
                        ) : ratesLoading ? (
                          <span
                            style={{
                              color: colors.gray500,
                              fontStyle: "italic",
                              fontSize: "12px",
                            }}
                          >
                            Loading...
                          </span>
                        ) : (
                          <span
                            style={{
                              color: colors.gray400,
                              fontStyle: "italic",
                              fontSize: "12px",
                            }}
                          >
                            N/A
                          </span>
                        )}
                      </td>
                      <td className="col">
                        {token.loadingBalance || ratesLoading ? (
                          <span
                            style={{
                              color: colors.gray500,
                              fontStyle: "italic",
                              fontSize: "12px",
                            }}
                          >
                            Loading...
                          </span>
                        ) : usdValue != null ? (
                          <strong>{formatUsd(usdValue)}</strong>
                        ) : (
                          <span
                            style={{
                              color: colors.gray400,
                              fontStyle: "italic",
                              fontSize: "12px",
                            }}
                          >-</span>
                        )}
                      </td>
                      <td className="col">
                        {token.loadingBalance || ratesLoading ? (
                          <span
                            style={{
                              color: colors.gray500,
                              fontStyle: "italic",
                              fontSize: "12px",
                            }}
                          >
                            Loading...
                          </span>
                        ) : pctOfPortfolio != null ? (
                          <span>{pctOfPortfolio.toFixed(1)}%</span>
                        ) : (
                          <span style={{ color: colors.gray400 }}>-</span>
                        )}
                      </td>
                    </>
                  )}
                  <td className="col">
                    <span
                      className={`status_tag ${
                        token.isOwned ? "active" : "inactive"
                      }`}
                    >
                      {token.isOwned ? "Associated" : "Not Associated"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {showUsdColumns && (
            <tfoot>
              <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                <td className="col" colSpan="5" style={{ textAlign: "right" }}>
                  Total Portfolio Value
                </td>
                <td className="col">
                  <strong style={{ color: colors.primary }}>
                    {ratesLoading ? (
                      <span
                        style={{
                          color: colors.gray500,
                          fontStyle: "italic",
                          fontSize: "12px",
                        }}
                      >
                        Calculating...
                      </span>
                    ) : (
                      formatUsd(totalBalanceUSD)
                    )}
                  </strong>
                </td>
                <td className="col">{ratesLoading ? "-" : "100%"}</td>
                <td className="col" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// Agent Wallets panel, the other agent/genesis wallets owned by this user,
// with quick-switch. Keeps the admin on this page instead of jumping back to
// the wallet list. Renders nothing while loading or when the user has none.
// ============================================================================

const AgentWalletsPanel = ({ wallet, onSelectWallet }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!wallet.user_id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getRequest(
          `${API_ENDPOINTS.AGENT_WALLETS}?user_id=${encodeURIComponent(
            wallet.user_id,
          )}`,
        );
        if (active) setRows(data && data.success ? data.data : []);
      } catch (error) {
        console.error("Error fetching agent wallets:", error);
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [wallet.user_id]);

  // Hide entirely until we know the user (no user_id → nothing to fetch) or
  // while the request is in flight, to avoid a flash of empty state.
  if (loading || !wallet.user_id) return null;

  const cell = {
    padding: "10px 14px",
    fontSize: "13px",
    borderBottom: "1px solid #eef0f4",
    textAlign: "left",
  };

  return (
    <div style={{ marginTop: "28px" }}>
      <h3
        style={{
          fontSize: "16px",
          fontWeight: 600,
          margin: "0 0 12px 0",
          color: "#1f2937",
        }}
      >
        Agent Wallets{wallet.email ? `, ${wallet.email}` : ""}
      </h3>
      {!rows.length ? (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "14px",
            fontSize: "13px",
            color: "#6b7280",
          }}
        >
          No agent wallets found for this user.
        </div>
      ) : (
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          overflowX: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th style={{ ...cell, fontWeight: 600, color: "#6b7280" }}>
                Agent Name
              </th>
              <th style={{ ...cell, fontWeight: 600, color: "#6b7280" }}>
                Address
              </th>
              <th style={{ ...cell, fontWeight: 600, color: "#6b7280" }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isCurrent = r.address === wallet.address;
              return (
                <tr key={r.address}>
                  <td style={{ ...cell, color: "#111827" }}>{r.name || "-"}</td>
                  <td style={{ ...cell }}>
                    <code style={{ color: "#374151" }}>{r.address}</code>
                  </td>
                  <td style={{ ...cell, textAlign: "right" }}>
                    {isCurrent ? (
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#059669",
                        }}
                      >
                        Viewing
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          onSelectWallet({
                            ...wallet,
                            address: r.address,
                            name: r.name,
                          })
                        }
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          color: "#2563eb",
                          fontSize: "13px",
                          fontWeight: 500,
                        }}
                      >
                        View balance →
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const BalanceDetail = ({ wallet, hederaChainId, onBack, onSelectWallet }) => {
  const [createdTokens, setCreatedTokens] = useState([]);
  const [ownedTokens, setOwnedTokens] = useState([]);
  const [availableTokens, setAvailableTokens] = useState([]);
  const [escrowData, setEscrowData] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("owned");

  // All fetch calls go through Rails proxy endpoints, no LP API credentials in the browser.
  const fetchBalances = useCallback(async () => {
    if (!wallet?.address) {
      setError("Missing wallet address");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Fetch all created tokens via Rails proxy
      let allTokens = await getRequest(API_ENDPOINTS.CREATED_TOKENS);
      if (!Array.isArray(allTokens) && Array.isArray(allTokens.data)) {
        allTokens = allTokens.data;
      }

      // Deduplicate tokens by symbol
      const seenSymbols = new Set();
      allTokens = allTokens.filter((token) => {
        if (seenSymbols.has(token.symbol)) return false;
        seenSymbols.add(token.symbol);
        return true;
      });

      setCreatedTokens(allTokens);

      // Step 2: Fetch all held balances (multi-token) via Rails proxy. This is
      // the full portfolio, Owned = tokens with balance > 0, regardless of
      // token association. Balances are already human-scaled by the mirror-node
      // endpoint, so no per-token calls are needed.
      const walletBalances = await getRequest(
        `${API_ENDPOINTS.WALLET_BALANCES}?address=${encodeURIComponent(
          wallet.address,
        )}`,
      );
      const heldById = new Map();
      for (const t of walletBalances?.tokens || []) {
        if ((parseFloat(t.balance) || 0) > 0) heldById.set(t.tokenId, t);
      }

      // created_tokens supplies symbol/name; join to the held balances by tokenId.
      const owned = [];
      const available = [];

      for (const token of allTokens) {
        const held = heldById.get(token.tokenId);
        if (held) {
          owned.push({
            tokenId: token.tokenId,
            symbol: token.symbol,
            name: token.name,
            decimals: token.decimals ?? held.decimals,
            rawBalance: String(held.balance),
            isOwned: true,
            loadingBalance: false,
            error: null,
          });
          heldById.delete(token.tokenId);
        } else {
          available.push({
            tokenId: token.tokenId,
            symbol: token.symbol,
            name: token.name,
            decimals: token.decimals,
            rawBalance: "0",
            isOwned: false,
            loadingBalance: false,
            error: null,
          });
        }
      }

      // Any held token missing from created_tokens is still a real holding.
      for (const [tokenId, held] of heldById) {
        owned.push({
          tokenId,
          symbol: held.symbol || tokenId,
          name: held.name || "",
          decimals: held.decimals,
          rawBalance: String(held.balance),
          isOwned: true,
          loadingBalance: false,
          error: null,
        });
      }

      setOwnedTokens(owned);
      setAvailableTokens(available);
      setLoading(false);

      const fetchEscrow = async () => {
        if (!wallet.user_id) return;
        try {
          const data = await getRequest(
            `${API_ENDPOINTS.ESCROW_BALANCE}?user_id=${encodeURIComponent(
              wallet.user_id,
            )}`,
          );
          setEscrowData(data);
          // 404 = no escrow, silently ignore
        } catch (err) {
          console.error("Error fetching escrow balance:", err);
          // Escrow is optional, don't fail the whole view
        }
      };

      // Step 5: Fetch exchange rates for owned tokens (runs in parallel)
      const fetchRates = async () => {
        const now = Date.now();
        if (rateCache.timestamp && now - rateCache.timestamp < rateCache.ttl) {
          setExchangeRates(rateCache.data);
          return;
        }

        setRatesLoading(true);
        const rates = {};
        // Unique fiat codes that need rate fetching
        const fiatCodes = new Set();
        const symbolToFiat = {};
        for (const token of owned) {
          const code = getFiatCode(token.symbol);
          if (code) {
            fiatCodes.add(code);
            symbolToFiat[token.symbol] = code;
          }
        }

        if (fiatCodes.size === 0) {
          setExchangeRates({});
          setRatesLoading(false);
          return;
        }

        try {
          const ratePromises = Array.from(fiatCodes).map(async (code) => {
            try {
              const data = await getRequest(
                `${API_ENDPOINTS.DYNAMIC_FEE}?currency=${encodeURIComponent(
                  code,
                )}`,
              );
              return { code, rate: parseFloat(data.buyRate) || 1 };
            } catch {
              return { code, rate: 1 };
            }
          });

          const rateResults = await Promise.all(ratePromises);
          const rateMap = {};
          for (const { code, rate } of rateResults) {
            rateMap[code] = rate;
          }
          // Map back to token symbols
          for (const [symbol, code] of Object.entries(symbolToFiat)) {
            rates[symbol] = rateMap[code] || 1;
          }

          rateCache.data = rates;
          rateCache.timestamp = now;
          setExchangeRates(rates);
        } finally {
          setRatesLoading(false);
        }
      };

      // Run Steps 3 + 4 + 5 in parallel
      await Promise.all([
        fetchEscrow(),
        fetchRates(),
      ]);
    } catch (err) {
      console.error("Error fetching user balances:", err);
      setError(err.message || "Failed to load balances");
      setLoading(false);
    }
  }, [wallet, hederaChainId]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  // Stats
  const escrowAmount = escrowData ? parseFloat(escrowData.escrow) || 0 : 0;

  // Owned/Available now come straight from the multi-token wallet-balances
  // endpoint (held balance > 0 = owned), so USDb is already included with its
  // real balance, no escrow merge needed. Total Balance = sum of held tokens
  // in USD (stablecoins 1:1; others converted via the fiat rate).
  const totalBalanceUSD = useMemo(() => {
    return ownedTokens.reduce((sum, token) => {
      if (token.loadingBalance) return sum;
      const humanBal = getHumanBalance(token.rawBalance);
      const fiatCode = getFiatCode(token.symbol);
      if (!fiatCode) {
        // Stablecoin: USDC, USD, USDb, 1:1
        return sum + humanBal;
      }
      const rate = exchangeRates[token.symbol];
      if (rate) return sum + humanBal / rate;
      return sum;
    }, 0);
  }, [ownedTokens, exchangeRates]);

  const stats = useMemo(() => {
    const items = [
      {
        value: createdTokens.length,
        label: "Total Tokens",
        gradient: gradients.primary,
      },
      {
        value: ownedTokens.length,
        label: "Owned",
        gradient: gradients.success,
      },
      {
        value: availableTokens.length,
        label: "Available",
        gradient: gradients.info,
      },
    ];
    // Total portfolio USD
    items.push({
      value:
        ratesLoading || balancesLoading
          ? "Calculating..."
          : formatUsd(totalBalanceUSD),
      label: "Total Balance",
      gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    });
    // Escrow stat if applicable
    if (escrowAmount > 0) {
      items.push({
        value: `$${escrowAmount.toFixed(2)}`,
        label: "In Escrow",
        gradient: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
      });
    }
    return items;
  }, [
    createdTokens,
    ownedTokens,
    availableTokens,
    totalBalanceUSD,
    escrowAmount,
    ratesLoading,
    balancesLoading,
  ]);

  // Loading state
  if (loading) {
    return (
      <div>
        <div style={commonStyles.sectionHeader}>
          <div>
            <h2
              style={{
                margin: "0 0 4px 0",
                color: colors.white,
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              User Balance
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {wallet.email}, {" "}
              <code style={{ color: "rgba(255,255,255,0.9)" }}>
                {wallet.address}
              </code>
            </p>
          </div>
          <button
            type="button"
            onClick={onBack}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Wallets
          </button>
        </div>
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: `3px solid ${colors.border}`,
              borderTopColor: colors.primary,
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: colors.gray600, fontSize: "14px" }}>
            Loading user balance...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div>
        <div style={commonStyles.sectionHeader}>
          <div>
            <h2
              style={{
                margin: "0 0 4px 0",
                color: colors.white,
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              User Balance
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {wallet.email}, {" "}
              <code style={{ color: "rgba(255,255,255,0.9)" }}>
                {wallet.address}
              </code>
            </p>
          </div>
          <button
            type="button"
            onClick={onBack}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Wallets
          </button>
        </div>
        <div
          className="flash flash_error"
          style={{ textAlign: "center", padding: "24px" }}
        >
          <p style={{ margin: "0 0 12px" }}>{error}</p>
          <button
            type="button"
            style={{
              ...commonStyles.button,
              ...commonStyles.buttonPrimary,
            }}
            onClick={fetchBalances}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={commonStyles.sectionHeader}>
        <div>
          <h2
            style={{
              margin: "0 0 4px 0",
              color: colors.white,
              fontSize: "20px",
              fontWeight: 600,
            }}
          >
            User Balance
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: "rgba(255,255,255,0.7)",
            }}
          >
            {wallet.name && (
              <strong style={{ color: colors.white }}>{wallet.name}</strong>
            )}
            {wallet.name && wallet.email && " · "}
            {wallet.email}
            {(wallet.name || wallet.email) && " "}, {" "}
            <code style={{ color: "rgba(255,255,255,0.9)" }}>
              {wallet.address}
            </code>
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={fetchBalances}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Refresh
          </button>
          <button
            type="button"
            onClick={onBack}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Wallets
          </button>
        </div>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        {stats.map((card) => (
          <div
            key={card.label}
            style={{
              ...statsCardStyles.card,
              background: card.gradient,
            }}
          >
            <span style={statsCardStyles.value}>{card.value}</span>
            <span style={statsCardStyles.label}>{card.label}</span>
          </div>
        ))}
      </div>

      {/* Escrow Summary Card */}
      <EscrowSummaryCard totalBalanceUSD={totalBalanceUSD} escrowData={escrowData} />

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          borderBottom: `1px solid ${colors.border}`,
          marginBottom: "0",
        }}
      >
        <TabButton
          active={activeTab === "owned"}
          label="Owned Assets"
          count={ownedTokens.length}
          onClick={() => setActiveTab("owned")}
        />
        <TabButton
          active={activeTab === "available"}
          label="Available Assets"
          count={availableTokens.length}
          onClick={() => setActiveTab("available")}
        />
      </div>

      {/* Token Table */}
      {activeTab === "owned" ? (
        <TokenTable
          tokens={ownedTokens}
          loading={balancesLoading}
          emptyMessage="No held assets found for this wallet"
          escrowData={escrowData}
          exchangeRates={exchangeRates}
          ratesLoading={ratesLoading}
          totalBalanceUSD={totalBalanceUSD}
        />
      ) : (
        <TokenTable
          tokens={availableTokens}
          loading={false}
          emptyMessage="All system tokens are associated with this wallet"
          exchangeRates={{}}
          totalBalanceUSD={0}
        />
      )}

      {/* Other agent wallets owned by this user, with quick-switch */}
      <AgentWalletsPanel wallet={wallet} onSelectWallet={onSelectWallet} />
    </div>
  );
};

export default BalanceDetail;
